// Funnel-creation wizard shell (FF-15 / MIS-998).
//
// `/funnels/new?domainId=X&step=N` — a 7-step machine whose resume point is
// DERIVED from FF-8's setup-status endpoint (server truth, never local state):
// landing with only a `domainId` jumps straight to the first incomplete step,
// and a refresh/deep-link/another device re-derives the same answer. Back
// navigation is free; forward navigation is gated — the requested `step` is
// clamped to the derived frontier at render time (no redirect loops while the
// status loads).
//
// Step BODIES are the real FF-16..FF-21 components (DomainStep, DnsWaitStep,
// LandersStep, IntegrationsStep, NumbersStep, FbBindingStep, ReviewChecklist),
// each consuming the same shared setup-status payload/cache slot. Nothing
// about wizard progress is persisted client-side.

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { DnsWaitStep } from '@/components/funnels/DnsWaitStep';
import { DomainStep } from '@/components/funnels/DomainStep';
import { FbBindingStep } from '@/components/funnels/FbBindingStep';
import { IntegrationsStep } from '@/components/funnels/IntegrationsStep';
import { LandersStep } from '@/components/funnels/LandersStep';
import { NumbersStep } from '@/components/funnels/NumbersStep';
import { ReviewChecklist } from '@/components/funnels/ReviewChecklist';
import { type DomainSetupStatus, useSetupStatus } from '@/components/funnels/useSetupStatus';
import { WizardErrorPanel } from '@/components/funnels/WizardErrorPanel';
import {
  clampStep,
  deriveStep,
  LAST_STEP,
  WIZARD_STEPS,
  WizardNav,
  type WizardStepId,
} from '@/components/funnels/WizardNav';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';

// Exported because the generated routeTree names this type for the route's
// search-param generics (TS4023 otherwise).
export interface WizardSearch {
  domainId?: number;
  step?: number;
}

// Mirrors ad/jobs.tsx `parseCreativeId`: coerce, validate, and DROP invalid
// params via conditional spread (exactOptionalPropertyTypes is ON).
function parseWizardSearch(search: Record<string, unknown>): WizardSearch {
  const rawDomain = search.domainId;
  const domainId = typeof rawDomain === 'number' ? rawDomain : Number(rawDomain);
  const rawStep = search.step;
  const step = typeof rawStep === 'number' ? rawStep : Number(rawStep);
  return {
    ...(Number.isInteger(domainId) && domainId > 0 ? { domainId } : {}),
    ...(Number.isInteger(step) && step >= 1 && step <= LAST_STEP ? { step } : {}),
  };
}

export const Route = createFileRoute('/_authenticated/funnels/new')({
  validateSearch: parseWizardSearch,
  component: FunnelWizardPage,
});

function FunnelWizardPage(): React.ReactElement {
  const navigate = useNavigate();
  const { domainId, step } = Route.useSearch();
  const { data: status, isLoading, isError, refetch } = useSetupStatus(domainId);

  // Server truth: the furthest reachable step. With no domain selected there is
  // nothing to derive (step 1); with one, the first failing check decides.
  const derivedStep = domainId === undefined ? 1 : deriveStep(status);
  // The shown step: the search param when present, else the derived resume
  // point — always clamped to the frontier (forward gated; back free).
  const currentStep = clampStep(step ?? derivedStep, derivedStep);

  const goToStep = (next: WizardStepId): void => {
    void navigate({
      to: '/funnels/new',
      search: {
        ...(domainId !== undefined ? { domainId } : {}),
        step: next,
      },
    });
  };

  // Hold at a loading panel until the status lands so the wizard's FIRST
  // rendered position is already the derived one (no step-1 flash → jump).
  const waitingOnStatus = domainId !== undefined && isLoading;

  return (
    <div className="space-y-6 p-8">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">New funnel</h1>
        <p className="text-sm text-gray-500">
          Domain, DNS, landers, integrations, numbers, ad binding — resumable any time; progress is
          derived from the live setup status{status ? ` for ${status.host}` : ''}.
        </p>
      </div>

      {waitingOnStatus ? (
        <p className="text-sm text-gray-500">Loading setup status…</p>
      ) : isError ? (
        // Deleted domain / lost access: offer recovery instead of a dead end —
        // retry the query, or clear the domainId back to step 1's picker.
        <WizardErrorPanel
          onRetry={() => void refetch()}
          onReset={() => void navigate({ to: '/funnels/new', search: {} })}
        />
      ) : (
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
          <aside className="w-full shrink-0 lg:w-56">
            <WizardNav
              currentStep={currentStep}
              derivedStep={derivedStep}
              onSelectStep={goToStep}
            />
          </aside>

          <section className="min-w-0 flex-1">
            <Card>
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle>
                  Step {currentStep} of {LAST_STEP} —{' '}
                  {WIZARD_STEPS[currentStep - 1]?.label ?? 'Review'}
                </CardTitle>
                {status ? (
                  <Badge variant={status.ready ? 'green' : 'gray'}>
                    {status.completed}/{status.total} checks
                  </Badge>
                ) : null}
              </CardHeader>
              <CardContent className="space-y-4">
                <StepBody step={currentStep} domainId={domainId} status={status} />

                <div className="flex items-center justify-between border-t border-[var(--border-soft)] pt-4">
                  <Button
                    variant="outline"
                    disabled={currentStep === 1}
                    onClick={() => goToStep(clampStep(currentStep - 1, derivedStep))}
                  >
                    Back
                  </Button>
                  {currentStep < LAST_STEP ? (
                    <Button
                      disabled={currentStep + 1 > derivedStep}
                      onClick={() => goToStep(clampStep(currentStep + 1, derivedStep))}
                    >
                      Continue
                    </Button>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          </section>
        </div>
      )}
    </div>
  );
}

/**
 * The real step bodies (FF-16..FF-21) — each consumes the shared setup-status
 * payload (or the same cache slot via useSetupStatus) rather than fetching its
 * own.
 */
function StepBody({
  step,
  domainId,
  status,
}: {
  step: WizardStepId;
  domainId: number | undefined;
  status: DomainSetupStatus | undefined;
}): React.ReactElement {
  switch (step) {
    case 1:
      return <DomainStep selectedDomainId={domainId} selectedHost={status?.host} />;
    case 2:
      return <DnsWaitStep domainId={domainId} />;
    case 3:
      return <LandersStep domainId={domainId} status={status} />;
    case 4:
      return <IntegrationsStep domainId={domainId} />;
    case 5:
      return <NumbersStep domainId={domainId} status={status} />;
    case 6:
      return <FbBindingStep domainId={domainId} status={status} />;
    default:
      return <ReviewChecklist domainId={domainId} />;
  }
}
